import { fetchWrapper } from "src/helpers/fetch-wrapper";

export type AppliedCoupon = {
  id: number;
  code: string;
  discount_percentage: number;
  discount_amount: number;
  original_amount: number;
  final_amount: number;
};

export const CMS_PLAN_STORAGE_KEY = "queryParamPlanId";
export const CMS_COUPON_STORAGE_KEY = "queryParamCouponCode";

type QueryValue = string | string[] | undefined;

const firstQueryValue = (value: QueryValue): string => {
  if (Array.isArray(value)) return String(value[0] || "").trim();
  return String(value || "").trim();
};

/** CMS sends `couponcode`; also accept `coupon` / `coupon_code`. */
export const getCouponFromQuery = (query: {
  couponcode?: QueryValue;
  coupon?: QueryValue;
  coupon_code?: QueryValue;
}): string =>
  (
    firstQueryValue(query.couponcode) ||
    firstQueryValue(query.coupon) ||
    firstQueryValue(query.coupon_code)
  ).toUpperCase();

export const getPlanIdFromQuery = (query: { plan_id?: QueryValue }): string =>
  firstQueryValue(query.plan_id);

/** Persist CMS plan/coupon so they survive login (never re-expose in URL). */
export const persistCmsCheckoutParams = (
  planId?: string | number | null,
  couponCode?: string | null,
) => {
  if (typeof window === "undefined") return;

  if (planId != null && String(planId).trim() !== "") {
    localStorage.setItem(CMS_PLAN_STORAGE_KEY, String(planId).trim());
  }

  const normalizedCoupon = String(couponCode || "")
    .trim()
    .toUpperCase();
  if (normalizedCoupon) {
    localStorage.setItem(CMS_COUPON_STORAGE_KEY, normalizedCoupon);
  }
};

export const clearCmsCouponOnly = () => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(CMS_COUPON_STORAGE_KEY);
};

export const clearCmsCheckoutParams = () => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(CMS_PLAN_STORAGE_KEY);
  localStorage.removeItem(CMS_COUPON_STORAGE_KEY);
  // Clean up any leftover signed-token keys from the previous approach.
  localStorage.removeItem("cmsCheckoutToken");
  localStorage.removeItem("cmsCheckoutIntent");
  try {
    sessionStorage.removeItem("cmsCheckoutPaymentDismissed");
    sessionStorage.removeItem("cmsAutoCheckoutStarted");
  } catch {
    // ignore
  }
};

export const readCmsPlanId = (): string =>
  (localStorage.getItem(CMS_PLAN_STORAGE_KEY) || "").trim();

export const readCmsCouponCode = (): string =>
  (localStorage.getItem(CMS_COUPON_STORAGE_KEY) || "").trim().toUpperCase();

export const hasCmsCheckoutIntent = (): boolean => Boolean(readCmsPlanId());

/** Payment-gateway return URLs include plan_id for verify — must not re-seed CMS checkout. */
export const isRazorpayPaymentReturnQuery = (query: {
  razorpay_payment_id?: QueryValue;
  razorpay_order_id?: QueryValue;
  razorpay_subscription_id?: QueryValue;
  razorpay_signature?: QueryValue;
}): boolean =>
  Boolean(
    firstQueryValue(query.razorpay_payment_id) ||
    firstQueryValue(query.razorpay_signature) ||
    firstQueryValue(query.razorpay_order_id) ||
    firstQueryValue(query.razorpay_subscription_id),
  );

/** Only capture CMS params on landing / subscription picker — never on payment-gateway. */
export const isCmsCheckoutCapturePath = (pathname: string): boolean => {
  const path = (pathname || "").split("?")[0];
  return path === "/" || path === "" || path === "/subscription";
};

/**
 * Capture CMS plan/coupon from the URL into localStorage (sync, no API).
 * Skips Razorpay payment-return URLs so plan_id on verify does not reopen checkout.
 * Returns true when params were present and stored.
 */
export const captureCmsParamsFromQuery = (
  query: {
    plan_id?: QueryValue;
    couponcode?: QueryValue;
    coupon?: QueryValue;
    coupon_code?: QueryValue;
    razorpay_payment_id?: QueryValue;
    razorpay_order_id?: QueryValue;
    razorpay_subscription_id?: QueryValue;
    razorpay_signature?: QueryValue;
  },
  pathname?: string,
): boolean => {
  if (isRazorpayPaymentReturnQuery(query)) return false;
  if (pathname != null && !isCmsCheckoutCapturePath(pathname)) return false;

  const planId = getPlanIdFromQuery(query);
  if (!planId) return false;
  persistCmsCheckoutParams(planId, getCouponFromQuery(query) || undefined);
  // Fresh CMS link overrides a prior payment-completed guard.
  try {
    sessionStorage.removeItem("cmsPaymentJustCompleted");
  } catch {
    // ignore
  }
  return true;
};

/**
 * True when the URL still exposes CMS plan/coupon — not payment-gateway verify params.
 */
export const urlHasCmsPlaintextParams = (
  asPathOrSearch: string,
  query?: {
    razorpay_payment_id?: QueryValue;
    razorpay_signature?: QueryValue;
    razorpay_order_id?: QueryValue;
    razorpay_subscription_id?: QueryValue;
  },
): boolean => {
  if (query && isRazorpayPaymentReturnQuery(query)) return false;
  if (/razorpay_payment_id=|razorpay_signature=/.test(asPathOrSearch)) {
    return false;
  }
  if (asPathOrSearch.includes("/subscription/payment-gateway")) return false;
  return (
    /[?&]plan_id=/.test(asPathOrSearch) ||
    /[?&]coupon(code|_code)?=/.test(asPathOrSearch)
  );
};

/**
 * Post-login redirect: clean /subscription — plan/coupon live in storage only.
 */
export const getCmsSubscriptionRedirectPath = (): string | null => {
  if (!readCmsPlanId()) return null;
  return "/subscription";
};

export async function validateCouponForPlan(args: {
  token: string;
  couponCode: string;
  planId?: number;
  amount?: number;
}): Promise<
  { valid: true; coupon: AppliedCoupon } | { valid: false; message: string }
> {
  try {
    const response = await fetchWrapper.post(
      "/api/coupons/validate",
      {
        coupon_code: args.couponCode,
        ...(args.planId != null ? { plan_id: args.planId } : {}),
        ...(args.amount != null ? { amount: args.amount } : {}),
      },
      {
        Authorization: `Bearer ${args.token}`,
        "Content-Type": "application/json",
      },
    );

    if (response.valid) {
      return {
        valid: true,
        coupon: {
          id: response.coupon_id,
          code: response.code,
          discount_percentage: response.discount_percentage,
          discount_amount: response.discount_amount,
          original_amount: response.original_amount,
          final_amount: response.final_amount,
        },
      };
    }

    return {
      valid: false,
      message: response.message || "Invalid coupon code.",
    };
  } catch (error) {
    return {
      valid: false,
      message: typeof error === "string" ? error : "Invalid coupon code.",
    };
  }
}
